import React, { useState } from 'react';
import { Button, DatePicker, Row, Col, Space, Dropdown, Modal } from 'antd';
import {  EyeFilled, WarningOutlined } from '@ant-design/icons';
// import { DownloadOutlined } from '@ant-design/icons';
import styles from './index.module.scss';
import ExpenseGroupPreviewJournal from 'components/ExpenseAllocation/PreviewJournal';
import ExpenseGroupSettingsModal from 'components/settings/ExpenseGroups/SettingsModal';
import OneOffSettingsModal from 'components/ExpenseAllocation/OneOffSettingsModal';
import ExportData from 'components/Global/ExportData';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from 'redux/store';
import { upsertExpenseGroupAllocationsBatchAction } from 'redux/action/expenseGroupAllocationAction';
import { getExpenseGroupsAction } from 'redux/action/expenseGroupAction';
import { clearTemporaryConfig } from 'redux/slice/expenseGroupAllocationSlice';
import { toastText } from 'utils/utils';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface Props {
	dateRange: [dayjs.Dayjs, dayjs.Dayjs] | null;
	expenseGroupOptions: { value: string; label: string }[];
	selectedExpenseGroup: string;
	onExpenseGroupChange?: (expenseGroupId: string) => void;
	allocationData?: any[];
	onAllocationSaved?: () => void;
	onSwitchToJournalEntries?: () => void;
	isTemporaryConfigWithNoData?: boolean;
	hasConfiguredAllocations?: boolean;
}

const AllocationSearchAndFilter: React.FC<Props> = ({
	dateRange,
	expenseGroupOptions,
	selectedExpenseGroup,
	allocationData,
	onAllocationSaved,
	onSwitchToJournalEntries,
	isTemporaryConfigWithNoData = false,
	hasConfiguredAllocations = false,
}) => {
	const dispatch = useDispatch<AppDispatch>();
	const selectedLabel =
		expenseGroupOptions.find((e: any) => e.value === selectedExpenseGroup)
			?.label || 'Select Expense Group';
	const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
	const [isSettingsOpen, setIsSettingsOpen] = useState(false);
	const [isOneOffSettingsOpen, setIsOneOffSettingsOpen] = useState(false);
	const [isAccountChangeModalOpen, setIsAccountChangeModalOpen] =
		useState<boolean>(false);
	const [hideSaveAsDraft, setHideSaveAsDraft] = useState<boolean>(false);
	const [isSavingAccounts, setIsSavingAccounts] = useState<boolean>(false);
	const [configWasSaved, setConfigWasSaved] = useState<boolean>(false);
	const [selectedForSettings, setSelectedForSettings] = useState<{
		id: string;
		name: string;
	} | null>(null);

	// Get temporary config and expense groups from Redux
	const { temporaryConfig } = useSelector(
		(state: any) => state.expenseGroupAllocation
	);
	const { data: expenseGroups } = useSelector(
		(state: any) => state.expenseGroups
	);
	const { oneOffConfigurations } = useSelector((state: any) => state.oneOff);
	const { data: chartOfAccounts } = useSelector(
		(state: any) => state.chartOfAccounts
	);

	// Check if One-Off mode is ON
	const isOneOffMode = selectedExpenseGroup === 'all';

	const openSettings = (expenseGroupId: string) => {
		if (!expenseGroupId) return;

		if (isOneOffMode) {
			// Open One-Off settings modal
			setIsOneOffSettingsOpen(true);
		} else {
			// Open normal expense group settings modal
			setSelectedForSettings({ id: expenseGroupId, name: selectedLabel });
			setIsSettingsOpen(true);
		}
	};

	const closeSettings = () => {
		setIsSettingsOpen(false);
		setSelectedForSettings(null);
	};

	const closeOneOffSettings = () => {
		setIsOneOffSettingsOpen(false);
	};

	// Check if allocation configuration has changed (accounts and/or allocations)
	const hasAllocationConfigChanged = (): boolean => {
		if (
			!temporaryConfig ||
			temporaryConfig.expenseGroupId !== selectedExpenseGroup
		) {
			return false;
		}

		// Find the saved expense group from Redux state
		const savedExpenseGroup = expenseGroups?.find(
			(group: any) => group.id === selectedExpenseGroup
		);

		if (!savedExpenseGroup) {
			return false;
		}

		// Check if accounts have changed
		const savedAccounts = savedExpenseGroup.accounts || [];
		const tempAccounts = temporaryConfig.configAccounts || [];
		const sortedSavedAccounts = [...savedAccounts].sort();
		const sortedTempAccounts = [...tempAccounts].sort();

		const accountsChanged =
			sortedSavedAccounts.length !== sortedTempAccounts.length ||
			sortedSavedAccounts.some(
				(account: string, index: number) =>
					account !== sortedTempAccounts[index]
			);

		// Check if allocation items have changed using expenseAllocations from expense group
		const savedAllocations = savedExpenseGroup.expenseAllocations || [];
		const tempItems = temporaryConfig.items || [];

		// Normalize items for comparison (ignore names, only compare IDs and percentages)
		const normalizeItem = (item: any) => ({
			percentage: Number(item.percentage) || 0,
			customerId: item.customerId || '',
			classId: item.classId || '',
			locationId: item.locationId || '',
		});

		const savedItemsNormalized = savedAllocations
			.map((alloc: any) =>
				normalizeItem({
					percentage: alloc.percentage || 0,
					customerId: alloc.customerId || '',
					classId: alloc.classId || '',
					locationId: alloc.locationId || '',
				})
			)
			.sort((a: any, b: any) => {
				const aKey = `${a.customerId}-${a.classId}-${a.locationId}`;
				const bKey = `${b.customerId}-${b.classId}-${b.locationId}`;
				return aKey.localeCompare(bKey);
			});

		const tempItemsNormalized = tempItems
			.map(normalizeItem)
			.sort((a: any, b: any) => {
				const aKey = `${a.customerId}-${a.classId}-${a.locationId}`;
				const bKey = `${b.customerId}-${b.classId}-${b.locationId}`;
				return aKey.localeCompare(bKey);
			});

		const itemsChanged =
			savedItemsNormalized.length !== tempItemsNormalized.length ||
			JSON.stringify(savedItemsNormalized) !==
			JSON.stringify(tempItemsNormalized);

		// Return true if either accounts or allocations have changed
		return accountsChanged || itemsChanged;
	};

	// Handle preview journal button click
	const handlePreviewJournalClick = () => {
		if (hasAllocationConfigChanged()) {
			// Show confirmation modal
			setIsAccountChangeModalOpen(true);
		} else {
			// No changes, open preview directly
			setHideSaveAsDraft(false);
			setConfigWasSaved(false); // No changes, so config wasn't saved
			setIsPreviewOpen(true);
		}
	};

	// Handle "Yes" - Save allocations configuration and open preview
	const handleSaveAccountsAndPreview = async () => {
		if (!temporaryConfig) return;

		setIsSavingAccounts(true);
		try {
			const isSingleAccountAllocation = temporaryConfig.singleAccountMode ?? false;

			// Build payload based on API requirements
			const payload: any = {
				expenseGroupId: temporaryConfig.expenseGroupId,
				items: temporaryConfig.items,
				isSingleAccountAllocation,
			};

			if (isSingleAccountAllocation) {
				// Switch is ON: send configAccounts (required, non-empty)
				// Don't send accounts field
				if (!temporaryConfig.configAccounts || temporaryConfig.configAccounts.length === 0) {
					toastText('Single account selection is required', 'error');
					setIsSavingAccounts(false);
					return;
				}
				payload.configAccounts = temporaryConfig.configAccounts;
			} else {
				// Switch is OFF: send accounts (required, can be empty array)
				// Get expense group accounts from Redux
				const selectedExpenseGroupData = expenseGroups?.find(
					(group: any) => group.id === temporaryConfig.expenseGroupId
				);
				payload.accounts = selectedExpenseGroupData?.accounts || [];
			}

			// Save allocation configuration
			await dispatch(upsertExpenseGroupAllocationsBatchAction(payload)).unwrap();

			// Refresh expense groups to reflect updated accounts in Expense Transaction filter
			await dispatch(getExpenseGroupsAction({ page: 1, limit: 150 }));

			// Clear temporary config after saving
			dispatch(clearTemporaryConfig());

			// Close modal and open preview
			setIsAccountChangeModalOpen(false);
			setHideSaveAsDraft(false);
			setConfigWasSaved(true); // Set flag indicating config was saved
			setIsPreviewOpen(true);
		} catch (error: any) {
			// Surface error to user
			toastText(
				error?.message || 'Failed to save allocation configuration',
				'error'
			);
		} finally {
			setIsSavingAccounts(false);
		}
	};

	// Handle "No" - Open preview without saving (hide Save as Draft)
	const handleSkipSaveAndPreview = () => {
		setIsAccountChangeModalOpen(false);
		setHideSaveAsDraft(true);
		setConfigWasSaved(false); // Config was not saved
		setIsPreviewOpen(true);
	};

	// Handle X button - Close modal without opening preview
	const handleCancelAccountChangeModal = () => {
		setIsAccountChangeModalOpen(false);
	};

	// Helper function to find account name by ID from chart of accounts
	const getAccountNameById = (accountId: string): string => {
		if (!accountId || !chartOfAccounts) return '';

		// Recursive function to search through tree structure
		const findAccount = (items: any[]): string | null => {
			for (const item of items || []) {
				if (item.Id === accountId) {
					return item.Name || item.DisplayName || item.FullyQualifiedName || item.title || '';
				}
				if (item.children && item.children.length > 0) {
					const found = findAccount(item.children);
					if (found) return found;
				}
			}
			return null;
		};

		return findAccount(chartOfAccounts) || '';
	};

	// Build export params for expenseAllocation module
	const buildExportParams = () => {
		if (!dateRange || !dateRange[0] || !dateRange[1]) {
			return {
				expenseGroupId: selectedExpenseGroup,
				fromDate: '',
				toDate: '',
			};
		}

		// Base params - always includes required fields
		const params: any = {
			expenseGroupId: selectedExpenseGroup,
			fromDate: dateRange[0].format('YYYY-MM-DD'),
			toDate: dateRange[1].format('YYYY-MM-DD'),
		};

		// Check if temporaryConfig exists for the selected expense group
		const hasTemporaryConfig =
			temporaryConfig &&
			temporaryConfig.expenseGroupId === selectedExpenseGroup;

		// Scenario: Has temporary config (tempFlag = true)
		if (hasTemporaryConfig) {
			// Get the selected expense group data to access accounts
			const selectedExpenseGroupData = expenseGroups?.find(
				(group: any) => group.id === selectedExpenseGroup
			);

			// Get isSingleAccountAllocation from temporaryConfig
			const isSingleAccountAllocation = temporaryConfig.singleAccountMode ?? false;

			// Build tempConfigAllocations array
			const tempConfigAllocations = (temporaryConfig.items || []).map((item: any) => ({
				percentage: Number(item.percentage) || 0,
				customerId: item.customerId || null,
				customerName: item.customerName || null,
				classId: item.classId || null,
				className: item.className || null,
				locationId: item.locationId || null,
				locationName: item.locationName || null,
			}));

			// Determine tempAccounts and configAccount based on switch state
			let tempAccounts: string[] = [];

			if (isSingleAccountAllocation) {
				// Switch is ON:
				// - configAccount = single selected account from modal (temporaryConfig.configAccounts[0])
				// - tempAccounts = expenseGroup.accounts (can be empty - backend handles it)
				const selectedAccounts = temporaryConfig.configAccounts || [];
				if (selectedAccounts.length > 0) {
					// Get expense group accounts for tempAccounts
					tempAccounts = selectedExpenseGroupData?.accounts || [];

					// configAccount is the single selected account from modal
					const configAccountId = selectedAccounts[0];
					const configAccountName = getAccountNameById(configAccountId);

					if (configAccountName) {
						params.configAccount = {
							name: configAccountName,
							value: configAccountId,
						};
					}
				}
			} else {
				// Switch is OFF:
				// - tempAccounts = selected accounts from modal multiple dropdown (temporaryConfig.configAccounts)
				// - No configAccount
				tempAccounts = temporaryConfig.configAccounts || [];
			}

			// Add temporary mode fields
			params.tempFlag = true;
			params.tempAccounts = tempAccounts;
			params.tempConfigAllocations = tempConfigAllocations;
			params.isSingleAccountAllocation = isSingleAccountAllocation;
		} else {
			// Scenario: No temporary config (tempFlag = false, normal mode)
			params.tempFlag = false;
		}

		return params;
	};

	return (
		<div className={styles['search-filter']}>
			<div className={styles['search-filter__wrapper']}>
				<Row justify="space-between" align="middle">
					<Col span={12}>
						<Space wrap>
							<Button
								className="expense-group-dropdown-button"
								aria-disabled
								tabIndex={-1}
								style={{
									pointerEvents: 'none',
									opacity: 0.6,
									// color: '#8c8c8c',
									backgroundColor: '#f5f5f5',
									borderColor: '#d9d9d9',
								}}
							>
								<span className="expense-group-button-text">
									{selectedLabel}
								</span>
							</Button>
							<RangePicker
								placeholder={['Start Date', 'End Date']}
								value={dateRange}
								format="MM/DD/YYYY"
								allowClear={false}
								inputReadOnly
								style={{
									minWidth: 250,
									height: 40,
									fontSize: '12px',
									pointerEvents: 'none',
									opacity: 0.6,
									// color: '#8c8c8c',
									backgroundColor: '#f5f5f5',
									borderColor: '#d9d9d9',
								}}
								size="large"
							/>
						</Space>
					</Col>
					<Col span={12} style={{ textAlign: 'right' }}>
						<Space>
							{/* Configure Allocations button - show for both One-Off and normal mode */}
							{((isOneOffMode && oneOffConfigurations?.expenseConfigAllocations?.length > 0) ||
								(!isOneOffMode &&
									selectedExpenseGroup !== 'all' &&
									((allocationData && allocationData.length > 0) ||
										((!allocationData || allocationData.length === 0) &&
											(hasConfiguredAllocations ||
												isTemporaryConfigWithNoData))))) && (
									<Button
										onClick={() => openSettings(selectedExpenseGroup)}
										style={{
											border: 'none',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											background: '#344735',
											fontSize: '12px',
											fontWeight: '600',
											borderRadius: '12px',
											cursor: 'pointer',
											minHeight: '41px',
											width: '180px',
											color: 'white',
										}}
									>
										Configure Allocations
									</Button>
								)}
							{allocationData && allocationData.length > 0 && (
								<>
									<Button
										onClick={handlePreviewJournalClick}
										style={{
											border: 'none',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											background: '#344735',
											fontSize: '12px',
											fontWeight: '600',
											borderRadius: '12px',
											cursor: 'pointer',
											minHeight: '41px',
											color: 'white',
											width: '180px',
										}}
									>
										Preview Journal
										<EyeFilled />
									</Button>
									<Dropdown trigger={['click']} placement="bottomRight">
										<ExportData
											moduleName={
												isOneOffMode
													? 'oneOffExpenseAllocation'
													: 'expenseAllocation'
											}
											params={
												isOneOffMode
													? {
														fromDate: dateRange?.[0]?.format('YYYY-MM-DD'),
														toDate: dateRange?.[1]?.format('YYYY-MM-DD'),
														oneOffConfigurations: oneOffConfigurations,
													}
													: buildExportParams()
											}
										/>
									</Dropdown>
								</>
							)}
						</Space>
						{isPreviewOpen && (
							<ExpenseGroupPreviewJournal
								isOpen={isPreviewOpen}
								onClose={() => {
									setIsPreviewOpen(false);
									setHideSaveAsDraft(false); // Reset on close
									setConfigWasSaved(false); // Reset on close
								}}
								selectedExpenseGroup={selectedExpenseGroup}
								dateRange={dateRange}
								expenseGroupOptions={expenseGroupOptions}
								onSwitchToJournalEntries={onSwitchToJournalEntries}
								hideSaveAsDraft={hideSaveAsDraft}
								configWasSaved={configWasSaved}
							/>
						)}

						{/* Account Change Confirmation Modal */}
						<Modal
							title={
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										gap: '12px',
										fontSize: '18px',
										fontWeight: 600,
										color: '#344735',
									}}
								>
									<WarningOutlined
										style={{ fontSize: '20px', color: '#ff9800',marginBottom: '4px'}}
									/>
									<span>Allocation Configuration Changed</span>
								</div>
							}
							open={isAccountChangeModalOpen}
							onCancel={handleCancelAccountChangeModal}
							footer={[
								<div
									key="footer"
									className={styles['account-change-modal__footer']}
								>
									<button
										key="no"
										onClick={handleSkipSaveAndPreview}
										className={styles['account-change-modal__button--cancel']}
										disabled={isSavingAccounts}
									>
										No
									</button>
									<button
										key="yes"
										onClick={handleSaveAccountsAndPreview}
										className={styles['account-change-modal__button--confirm']}
										disabled={isSavingAccounts}
									>
										{isSavingAccounts ? (
											<img src="/assets/gifs/loading-black.gif" height={30} />
										) : (
											'Yes'
										)}
									</button>
								</div>,
							]}
							width={520}
							centered
							closable={true}
							style={{ top: 20 }}
						>
							<div
								style={{
									padding: '16px 0',
									display: 'flex',
									flexDirection: 'column',
									gap: '8px',
								}}
							>

								<p
									style={{
										fontSize: '12px',
										margin: 0,
										lineHeight: '1.6',
									}}
								>
									You have made changes to the allocation configuration.
								</p>
								<p
									style={{
										fontSize: '12px',
										margin: 0,
										lineHeight: '1.6',
									}}
								>
									Would you like to save these changes before previewing the
									journal?
								</p>
							</div>
						</Modal>

						{isOneOffMode && isOneOffSettingsOpen && (
							<OneOffSettingsModal
								isModalOpen={isOneOffSettingsOpen}
								handleCancel={closeOneOffSettings}
								onAllocationSaved={onAllocationSaved}
							/>
						)}

						{!isOneOffMode && isSettingsOpen && (
							<ExpenseGroupSettingsModal
								isModalOpen={isSettingsOpen}
								handleCancel={closeSettings}
								expenseGroup={selectedForSettings}
								onAllocationSaved={onAllocationSaved}
								isTemporaryMode={true}
							/>
						)}
					</Col>
				</Row>
			</div>
		</div>
	);
};

export default AllocationSearchAndFilter;
